import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { navLinks } from "@/mocks/landing";
import { useAuth } from "@/hooks/useAuth";

export default function Navbar() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : "";

  const handleSignOut = () => {
    signOut();
    setMenuOpen(false);
    navigate("/");
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/90 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <span
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
              scrolled ? "bg-primary-500" : "bg-background-50"
            }`}
          >
            <i
              className={`ri-leaf-line text-lg ${
                scrolled ? "text-background-50" : "text-primary-600"
              }`}
            ></i>
          </span>
          <span
            className={`font-heading text-xl font-semibold tracking-tight ${
              scrolled ? "text-foreground-950" : "text-background-50"
            }`}
          >
            PureNest
          </span>
        </Link>

        <ul className="hidden md:flex items-center gap-9">
          {navLinks.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled
                    ? "text-foreground-700 hover:text-primary-600"
                    : "text-background-50/90 hover:text-background-50"
                }`}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-colors cursor-pointer ${
                  scrolled
                    ? "border-background-200 hover:bg-background-100"
                    : "border-background-50/30 hover:bg-background-50/10"
                }`}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
                  {initials}
                </span>
                <span
                  className={`text-sm font-medium whitespace-nowrap ${
                    scrolled ? "text-foreground-900" : "text-background-50"
                  }`}
                >
                  {user.firstName}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform ${
                    menuOpen ? "rotate-180" : ""
                  } ${scrolled ? "text-foreground-700" : "text-background-50"}`}
                ></i>
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-background-200">
                    <p className="text-sm font-medium text-foreground-950">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-foreground-600 truncate">{user.email}</p>
                  </div>
                  <div className="py-1.5">
                    <Link
                      to="/dashboard"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                    >
                      <i className="ri-dashboard-line text-foreground-600"></i>
                      Dashboard
                    </Link>
                    <Link
                      to="/dashboard/bookings"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                    >
                      <i className="ri-calendar-check-line text-foreground-600"></i>
                      My bookings
                    </Link>
                    <Link
                      to="/dashboard/cleaners"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                    >
                      <i className="ri-user-heart-line text-foreground-600"></i>
                      Favorite cleaners
                    </Link>
                    <Link
                      to="/dashboard/billing"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                    >
                      <i className="ri-bank-card-line text-foreground-600"></i>
                      Billing
                    </Link>
                  </div>
                  <div className="border-t border-background-200 py-1.5">
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
                    >
                      <i className="ri-logout-box-r-line text-foreground-600"></i>
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className={`text-sm font-medium whitespace-nowrap transition-colors ${
                scrolled
                  ? "text-foreground-800 hover:text-primary-600"
                  : "text-background-50 hover:text-background-100"
              }`}
            >
              Sign in
            </Link>
          )}
          <Link
            to="/booking"
            className="group flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 pl-5 pr-2 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
          >
            Book a clean
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50">
              <i className="ri-arrow-right-up-line text-primary-600 text-sm"></i>
            </span>
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full ${
            scrolled ? "bg-background-100 text-foreground-900" : "bg-background-50/20 text-background-50"
          }`}
          aria-label="Toggle menu"
        >
          <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-6 space-y-4">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block text-foreground-800 font-medium"
            >
              {l.label}
            </a>
          ))}
          {user ? (
            <>
              <div className="flex items-center gap-3 pt-2 border-t border-background-200">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
                  {initials}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-foreground-600">{user.email}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setOpen(false);
                  handleSignOut();
                }}
                className="block text-foreground-800 font-medium cursor-pointer"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="block text-foreground-800 font-medium"
                onClick={() => setOpen(false)}
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="block text-foreground-800 font-medium"
                onClick={() => setOpen(false)}
              >
                Create account
              </Link>
            </>
          )}
          <Link
            to="/booking"
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap"
          >
            Book a clean <i className="ri-arrow-right-up-line"></i>
          </Link>
        </div>
      )}
    </header>
  );
}